import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimeClock } from '@mui/x-date-pickers/TimeClock';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import './DateTimeRangePicker.css';

interface DateTimeRangePickerProps {
  startTime: string; // ISO string
  endTime: string;   // ISO string
  onChange: (start: string, end: string) => void;
  disabled?: boolean;
  disableStart?: boolean;
  disableEnd?: boolean;
  duration?: number;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const theme = createTheme({
  palette: {
    primary: {
      main: "#1D4C3D", // Matches the green theme
    },
  },
  typography: {
    fontFamily: 'inherit',
  }
});

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  startTime,
  endTime,
  onChange,
  disabled = false,
  disableStart = false,
  disableEnd = false,
  duration,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Parse initial dates or use current date
  const initialStart = startTime ? new Date(startTime) : new Date();
  const initialEnd = endTime ? new Date(endTime) : new Date(new Date().setHours(new Date().getHours() + 1));

  const [startD, setStartD] = useState(initialStart);
  const [endD, setEndD] = useState(initialEnd);
  
  const [viewStartYear, setViewStartYear] = useState(initialStart.getFullYear());
  const [viewStartMonth, setViewStartMonth] = useState(initialStart.getMonth());
  
  const [viewEndYear, setViewEndYear] = useState(initialEnd.getFullYear());
  const [viewEndMonth, setViewEndMonth] = useState(initialEnd.getMonth());

  const [startMode, setStartMode] = useState<'date'|'time'>('date');
  const [endMode, setEndMode] = useState<'date'|'time'>('date');

  const [startClockView, setStartClockView] = useState<'hours' | 'minutes'>('hours');
  const [endClockView, setEndClockView] = useState<'hours' | 'minutes'>('hours');

  // Input strings for TIME
  const formatTime = (d: Date) => d.toTimeString().slice(0,5);
  const [startTimeStr, setStartTimeStr] = useState(formatTime(initialStart));
  const [endTimeStr, setEndTimeStr] = useState(formatTime(initialEnd));

  const applyDurationShift = (newStartD: Date, newStartTimeStr: string) => {
    if (duration) {
      const finalStart = new Date(newStartD);
      const [sh, sm] = newStartTimeStr.split(':').map(Number);
      if (!isNaN(sh) && !isNaN(sm)) {
        finalStart.setHours(sh, sm, 0, 0);
      }
      const newEnd = new Date(finalStart.getTime() + duration * 60000);
      setEndD(newEnd);
      setEndTimeStr(formatTime(newEnd));
      setViewEndYear(newEnd.getFullYear());
      setViewEndMonth(newEnd.getMonth());
    }
  };

  useEffect(() => {
    if (startTime) {
      const d = new Date(startTime);
      setStartD(d);
      setStartTimeStr(formatTime(d));
      setViewStartYear(d.getFullYear());
      setViewStartMonth(d.getMonth());
    }
    if (endTime) {
      const d = new Date(endTime);
      setEndD(d);
      setEndTimeStr(formatTime(d));
      setViewEndYear(d.getFullYear());
      setViewEndMonth(d.getMonth());
    }
  }, [startTime, endTime]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const handleApply = () => {
    let localIsoStart = startTime;
    if (!disableStart) {
      const finalStart = new Date(startD);
      const [sh, sm] = startTimeStr.split(':').map(Number);
      if (!isNaN(sh) && !isNaN(sm)) {
        finalStart.setHours(sh, sm, 0, 0);
      }
      localIsoStart = new Date(finalStart.getTime() - finalStart.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }

    let localIsoEnd = endTime;
    if (!disableEnd) {
      const finalEnd = new Date(endD);
      const [eh, em] = endTimeStr.split(':').map(Number);
      if (!isNaN(eh) && !isNaN(em)) {
        finalEnd.setHours(eh, em, 0, 0);
      }
      localIsoEnd = new Date(finalEnd.getTime() - finalEnd.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }

    onChange(localIsoStart, localIsoEnd);
    setIsOpen(false);
  };

  const handleReset = () => {
    const now = new Date();
    setStartD(now);
    setStartTimeStr(formatTime(now));
    
    const anHourLater = new Date(now.getTime() + 60*60*1000);
    setEndD(anHourLater);
    setEndTimeStr(formatTime(anHourLater));

    setViewStartYear(now.getFullYear());
    setViewStartMonth(now.getMonth());
    setViewEndYear(anHourLater.getFullYear());
    setViewEndMonth(anHourLater.getMonth());
    
    setStartMode('date');
    setEndMode('date');
  };

  const renderCalendar = (
    year: number, month: number, 
    selectedDate: Date, 
    onSelect: (d: Date) => void
  ) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    // Previous month filler
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="dtrp-day empty"></div>);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const isSelected = 
        selectedDate.getDate() === d &&
        selectedDate.getMonth() === month &&
        selectedDate.getFullYear() === year;

      days.push(
        <div 
          key={d} 
          className={`dtrp-day ${isSelected ? 'selected' : ''}`}
          onClick={() => {
            const newD = new Date(year, month, d);
            onSelect(newD);
          }}
        >
          <span>{d}</span>
        </div>
      );
    }

    return (
      <div className="dtrp-calendar">
        <div className="dtrp-weekdays">
          {['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'].map(w => <div key={w}>{w}</div>)}
        </div>
        <div className="dtrp-days-grid">
          {days}
        </div>
      </div>
    );
  };

  const displayFormat = (dateStr: string) => {
    if (!dateStr) return 'Select Date & Time';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Select Date & Time';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' });
  };

  const formatDateLabel = (d: Date) => {
    return `${d.getDate()} ${MONTHS[d.getMonth()].substring(0,3)}, ${d.getFullYear()}`;
  };

  const isTriggerDisabled = disabled || (disableStart && disableEnd);

  return (
    <div className="dtrp-container">
      <div 
        className={`dtrp-trigger-box ${isTriggerDisabled ? 'disabled' : ''}`}
        onClick={() => !isTriggerDisabled && setIsOpen(true)}
      >
        <div className="dtrp-trigger-half" style={{ opacity: disableStart ? 0.6 : 1, cursor: disableStart ? 'not-allowed' : 'pointer' }}>
          <label>{disableStart ? 'START TIME (LOCKED)' : 'START TIME'}</label>
          <div className="val">{startTime ? displayFormat(startTime) : 'Select Start'}</div>
        </div>
        <div className="dtrp-trigger-divider" />
        <div className="dtrp-trigger-half" style={{ opacity: disableEnd ? 0.6 : 1, cursor: disableEnd ? 'not-allowed' : 'pointer' }}>
          <label>{disableEnd ? 'END TIME (LOCKED)' : 'END TIME'}</label>
          <div className="val">{endTime ? displayFormat(endTime) : 'Select End'}</div>
        </div>
      </div>

      {isOpen && (
        <>
          <div className="dtrp-backdrop" onClick={() => setIsOpen(false)} />
          <div className="dtrp-popover">
            <div className="dtrp-popover-content">
              
              {/* LEFT SIDE: START */}
              <div className="dtrp-pane" style={{ pointerEvents: disableStart ? 'none' : 'auto', opacity: disableStart ? 0.45 : 1 }}>
                <div className="dtrp-inputs-row">
                  <div 
                    className={`dtrp-input-box ${startMode === 'date' ? 'active' : ''}`}
                    onClick={() => !disableStart && setStartMode('date')}
                    style={{ cursor: disableStart ? 'not-allowed' : 'pointer' }}
                  >
                    <label>DATE {disableStart ? '(LOCKED)' : ''}</label>
                    <div className="val">{formatDateLabel(startD)}</div>
                  </div>
                  <div 
                    className={`dtrp-input-box ${startMode === 'time' ? 'active' : ''}`}
                    onClick={() => !disableStart && setStartMode('time')}
                    style={{ cursor: disableStart ? 'not-allowed' : 'pointer' }}
                  >
                    <label>TIME {disableStart ? '(LOCKED)' : ''}</label>
                    <div className="val">{dayjs(`2000-01-01T${startTimeStr}`).format('hh:mm A')}</div>
                  </div>
                </div>

                {startMode === 'date' ? (
                  <>
                    <div className="dtrp-month-nav">
                      <button onClick={() => {
                        if (viewStartMonth === 0) { setViewStartMonth(11); setViewStartYear(viewStartYear-1); }
                        else setViewStartMonth(viewStartMonth-1);
                      }}><ChevronLeft size={20}/></button>
                      <div className="dtrp-month-label">{MONTHS[viewStartMonth]} {viewStartYear}</div>
                      <button onClick={() => {
                        if (viewStartMonth === 11) { setViewStartMonth(0); setViewStartYear(viewStartYear+1); }
                        else setViewStartMonth(viewStartMonth+1);
                      }}><ChevronRight size={20}/></button>
                    </div>
                    {renderCalendar(viewStartYear, viewStartMonth, startD, (d) => { 
                      setStartD(d); 
                      applyDurationShift(d, startTimeStr);
                      setStartMode('time'); 
                    })}
                  </>
                ) : (
                  <div className="dtrp-time-wrapper">
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
                      <button 
                        onClick={() => setStartClockView('hours')} 
                        style={{ fontWeight: startClockView === 'hours' ? 700 : 500, color: startClockView === 'hours' ? '#1D4C3D' : '#94a3b8', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', borderBottom: startClockView === 'hours' ? '2px solid #1D4C3D' : '2px solid transparent', paddingBottom: '4px' }}
                      >
                        Hours
                      </button>
                      <button 
                        onClick={() => setStartClockView('minutes')} 
                        style={{ fontWeight: startClockView === 'minutes' ? 700 : 500, color: startClockView === 'minutes' ? '#1D4C3D' : '#94a3b8', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', borderBottom: startClockView === 'minutes' ? '2px solid #1D4C3D' : '2px solid transparent', paddingBottom: '4px' }}
                      >
                        Minutes
                      </button>
                    </div>
                    <ThemeProvider theme={theme}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimeClock 
                          view={startClockView}
                          onViewChange={(newView) => {
                            if (newView === 'hours' || newView === 'minutes') setStartClockView(newView);
                          }}
                          value={dayjs(`2000-01-01T${startTimeStr}`)}
                          onChange={(newTime) => { 
                            if (newTime) {
                              const newTimeStr = newTime.format('HH:mm');
                              setStartTimeStr(newTimeStr); 
                              applyDurationShift(startD, newTimeStr);
                            }
                          }}
                          ampm={true}
                          ampmInClock={true}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                  </div>
                )}
              </div>

              {/* DIVIDER */}
              <div className="dtrp-divider" />

              {/* RIGHT SIDE: END */}
              <div className="dtrp-pane" style={{ pointerEvents: disableEnd ? 'none' : 'auto', opacity: disableEnd ? 0.45 : 1 }}>
                <div className="dtrp-inputs-row">
                  <div 
                    className={`dtrp-input-box ${endMode === 'date' ? 'active' : ''}`}
                    onClick={() => !disableEnd && setEndMode('date')}
                    style={{ cursor: disableEnd ? 'not-allowed' : 'pointer' }}
                  >
                    <label>DATE</label>
                    <div className="val">{formatDateLabel(endD)}</div>
                  </div>
                  <div 
                    className={`dtrp-input-box ${endMode === 'time' ? 'active' : ''}`}
                    onClick={() => setEndMode('time')}
                    style={{ cursor: 'pointer' }}
                  >
                    <label>TIME</label>
                    <div className="val">{dayjs(`2000-01-01T${endTimeStr}`).format('hh:mm A')}</div>
                  </div>
                </div>

                {endMode === 'date' ? (
                  <>
                    <div className="dtrp-month-nav">
                      <button onClick={() => {
                        if (viewEndMonth === 0) { setViewEndMonth(11); setViewEndYear(viewEndYear-1); }
                        else setViewEndMonth(viewEndMonth-1);
                      }}><ChevronLeft size={20}/></button>
                      <div className="dtrp-month-label">{MONTHS[viewEndMonth]} {viewEndYear}</div>
                      <button onClick={() => {
                        if (viewEndMonth === 11) { setViewEndMonth(0); setViewEndYear(viewEndYear+1); }
                        else setViewEndMonth(viewEndMonth+1);
                      }}><ChevronRight size={20}/></button>
                    </div>
                    {renderCalendar(viewEndYear, viewEndMonth, endD, (d) => { setEndD(d); setEndMode('time'); })}
                  </>
                ) : (
                  <div className="dtrp-time-wrapper">
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
                      <button 
                        onClick={() => setEndClockView('hours')} 
                        style={{ fontWeight: endClockView === 'hours' ? 700 : 500, color: endClockView === 'hours' ? '#1D4C3D' : '#94a3b8', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', borderBottom: endClockView === 'hours' ? '2px solid #1D4C3D' : '2px solid transparent', paddingBottom: '4px' }}
                      >
                        Hours
                      </button>
                      <button 
                        onClick={() => setEndClockView('minutes')} 
                        style={{ fontWeight: endClockView === 'minutes' ? 700 : 500, color: endClockView === 'minutes' ? '#1D4C3D' : '#94a3b8', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', borderBottom: endClockView === 'minutes' ? '2px solid #1D4C3D' : '2px solid transparent', paddingBottom: '4px' }}
                      >
                        Minutes
                      </button>
                    </div>
                    <ThemeProvider theme={theme}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimeClock 
                          view={endClockView}
                          onViewChange={(newView) => {
                            if (newView === 'hours' || newView === 'minutes') setEndClockView(newView);
                          }}
                          value={dayjs(`2000-01-01T${endTimeStr}`)}
                          onChange={(newTime) => { if (newTime) setEndTimeStr(newTime.format('HH:mm')); }}
                          ampm={true}
                          ampmInClock={true}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                  </div>
                )}
              </div>

            </div>

            <div className="dtrp-footer">
              <button className="dtrp-btn-reset" onClick={handleReset}>Reset</button>
              <button className="dtrp-btn-okay" onClick={handleApply}>Okay</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateTimeRangePicker;
